import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Buildings, Plus, ArrowRight, Trash, FileText, UploadSimple } from "@phosphor-icons/react";

export default function PortalAsesor() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [nombre, setNombre] = useState("");
    const [cif, setCif] = useState("");
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true); setError("");
        try {
            const { data } = await api.get("/asesor/clientes");
            setItems(data.items || []);
        } catch (e) {
            setError(formatApiError(e, "Error al cargar clientes"));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function createCliente(e) {
        e.preventDefault();
        if (!nombre.trim()) { setError("El nombre es obligatorio"); return; }
        setSaving(true); setError("");
        try {
            const { data } = await api.post("/asesor/clientes", { nombre, cif });
            setShowNew(false); setNombre(""); setCif("");
            await load();
            nav(`/portal-asesor/${data.id}`);
        } catch (e) {
            setError(formatApiError(e, "Error al crear cliente"));
        } finally {
            setSaving(false);
        }
    }

    async function eliminar(c) {
        if (!confirm(`¿Eliminar "${c.nombre}" y todas sus facturas? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/asesor/clientes/${c.id}`);
            await load();
        } catch (e) {
            alert(formatApiError(e, "Error al eliminar"));
        }
    }

    return (
        <div className="min-h-screen bg-[var(--gf-bg)]">
            <Navbar />
            <main className="max-w-6xl mx-auto px-4 py-10">
                <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
                    <div>
                        <div className="micro-label text-[var(--gf-gold)] mb-2">Portal del Asesor</div>
                        <h1 className="serif text-3xl md:text-4xl text-[var(--gf-navy)]">Mis clientes</h1>
                        <p className="text-sm text-[var(--gf-text-secondary)] mt-2 max-w-2xl">
                            Sube ZIPs con facturas mezcladas y clasifícalas automáticamente por empresa, ejercicio y trimestre. Descarga los libros IVA en Excel.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to="/portal-asesor/subida-masiva" className="btn-outline inline-flex items-center gap-2" data-testid="btn-subida-masiva" title="Sube un ZIP con facturas mezcladas de varios clientes y deja que la máquina las reparta por CIF">
                            <UploadSimple size={14} weight="bold" /> Subida Masiva
                        </Link>
                        <button onClick={() => setShowNew(true)} className="btn-gold" data-testid="btn-new-cliente">
                            <Plus size={16} weight="bold" />
                            Nuevo cliente
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-4" data-testid="error-msg">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="py-16 text-center"><span className="elegant-loader"><span /><span /><span /></span></div>
                ) : items.length === 0 ? (
                    <div className="gf-card text-center py-12" data-testid="empty-state">
                        <Buildings size={48} className="mx-auto text-[var(--gf-gold)] mb-4" />
                        <h3 className="serif text-xl mb-2">Aún no tienes clientes</h3>
                        <p className="text-sm text-[var(--gf-text-secondary)] mb-4">Da de alta tu primer cliente para empezar a clasificar sus facturas.</p>
                        <button onClick={() => setShowNew(true)} className="btn-gold" data-testid="btn-empty-new">
                            <Plus size={14} /> Nuevo cliente
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((c) => (
                            <div key={c.id} className="gf-card hover:shadow-md transition-shadow" data-testid={`cliente-card-${c.id}`}>
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="serif text-lg text-[var(--gf-navy)] truncate" title={c.nombre}>{c.nombre}</h3>
                                        <div className="mono text-xs text-[var(--gf-text-muted)] mt-1">{c.cif || "sin CIF"}</div>
                                    </div>
                                    <button onClick={() => eliminar(c)} className="p-1 text-[var(--gf-danger)] hover:opacity-70" title="Eliminar" data-testid={`btn-delete-${c.id}`}>
                                        <Trash size={16} />
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-[var(--gf-text-secondary)] mb-4">
                                    <FileText size={14} />
                                    {c.facturas_count || 0} facturas
                                </div>
                                <Link to={`/portal-asesor/${c.id}`} className="btn-outline w-full justify-center" data-testid={`btn-open-${c.id}`}>
                                    Abrir <ArrowRight size={14} />
                                </Link>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal nuevo cliente */}
                {showNew && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowNew(false)}>
                        <form onClick={(e) => e.stopPropagation()} onSubmit={createCliente} className="bg-white p-6 md:p-8 max-w-md w-full space-y-4" data-testid="modal-new-cliente">
                            <h2 className="serif text-2xl text-[var(--gf-navy)]">Nuevo cliente</h2>
                            <div>
                                <label className="gf-label">Nombre / Razón social *</label>
                                <input value={nombre} onChange={(e) => setNombre(e.target.value)} className="gf-input" required data-testid="input-nombre" autoFocus />
                            </div>
                            <div>
                                <label className="gf-label">CIF / NIF</label>
                                <input value={cif} onChange={(e) => setCif(e.target.value)} className="gf-input mono" placeholder="B12345678" data-testid="input-cif" />
                                <p className="text-xs text-[var(--gf-text-muted)] mt-1">Se usará para detectar automáticamente qué facturas son emitidas y cuáles recibidas.</p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowNew(false)} className="btn-outline flex-1">Cancelar</button>
                                <button type="submit" disabled={saving} className="btn-gold flex-1" data-testid="btn-save-cliente">
                                    {saving ? "Creando..." : "Crear cliente"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
}
